import type { Log } from "../log/Log";
import type { TweenEasing } from "./Easing";
import { resolveEasing } from "./easingFunctions";
import type { EasingFunction } from "./easingFunctions";
import type { PresentationClockPort } from "./FixedPresentationClock";
import type { PresentationTimerDriver, TimerSleep } from "./PresentationTimerDriver";
import type { PresentationTicker } from "./PresentationTicker";

type ScopeId = string;

/** Minimal 2D vector; matches Pixi's ObservablePoint/Point shape without importing it. */
export interface Vec2Like {
  readonly x: number;
  readonly y: number;
}

/**
 * Anything with settable `position` / `scale` points — a Pixi Container, or a
 * plain object in tests. The runner only reads `.x/.y` and calls `.set(x, y)`.
 */
export interface TweenTarget {
  position: { x: number; y: number; set(x: number, y?: number): unknown };
  scale: { x: number; y: number; set(x: number, y?: number): unknown };
}

export type TweenProps = {
  position?: Vec2Like;
  scale?: Vec2Like;
};

export type TweenOptions = {
  easing?: TweenEasing | EasingFunction;
};

export type TweenProgressOptions = {
  easing?: TweenEasing | EasingFunction;
  /** If true (default): when slammed/canceled, do one final update(1). */
  finishOnCancel?: boolean;
};

type Cancelable = () => void;

interface RunningTween {
  stop(): void;
}

/**
 * PresentationTaskRunner — the ONLY animation engine for presentation code.
 *
 * Contract:
 * - scope-aware: `runInScope` windows, `slamFinishScope`, `armSlamNextRun`, `clearScope`
 * - every duration is pre-scaled ONCE at start by the PresentationClock (or a
 *   per-scope fixed scale), so a speed-mode change never distorts an in-flight tween
 * - many concurrent `tweenProgress` calls in one scope; no cross-cancellation
 * - `tweenTo` owns position/scale channels per target and never stops foreign tweens
 *
 * The tween is in-house and driven by the
 * PresentationTicker (pause / frame-step / deterministic tests), `tweenTo`
 * targets `{ position, scale }` points, and
 * `tickInScope` needs no re-arm windows (there is no first-tick frame drop).
 */
export class PresentationTaskRunner {
  private readonly clock: PresentationClockPort;
  private readonly log?: Log;
  private readonly timer: PresentationTimerDriver;
  private readonly ticker: PresentationTicker;

  private readonly cancelByScope: Map<ScopeId, Set<Cancelable>> = new Map();
  private readonly slammedScopes: Set<ScopeId> = new Set();
  private readonly scaleByScope: Map<ScopeId, number> = new Map();
  private readonly slamFinishSeconds: number;

  private readonly activePositionTweenByTarget: WeakMap<TweenTarget, RunningTween> = new WeakMap();
  private readonly activeScaleTweenByTarget: WeakMap<TweenTarget, RunningTween> = new WeakMap();

  private readonly armedNextRunScopes: Set<ScopeId> = new Set();
  /** Open `runInScope` windows per scope: the scope's state is cleared when the LAST one closes. */
  private readonly openWindowsByScope: Map<ScopeId, number> = new Map();

  public constructor(input: {
    clock: PresentationClockPort;
    timer: PresentationTimerDriver;
    log?: Log;
    slamFinishSeconds?: number;
  }) {
    this.clock = input.clock;
    this.timer = input.timer;
    this.ticker = input.timer.ticker;
    this.log = input.log;
    this.slamFinishSeconds = input.slamFinishSeconds ?? 0.04;
  }

  public async runInScope(scopeId: ScopeId, fn: () => Promise<void>): Promise<void> {
    this.ensureScope(scopeId);
    this.openWindowsByScope.set(scopeId, (this.openWindowsByScope.get(scopeId) ?? 0) + 1);

    const armed = this.armedNextRunScopes.has(scopeId);
    if (armed) {
      this.armedNextRunScopes.delete(scopeId);
      this.slammedScopes.add(scopeId);
    }

    try {
      await fn();
    } finally {
      // Windows on one scope may overlap. Until 2026-09-26 the first to close wiped the scope, so the
      // other's running work was no longer counted and a slam found nothing to finish (review 46).
      const stillOpen = (this.openWindowsByScope.get(scopeId) ?? 1) - 1;
      if (stillOpen > 0) {
        this.openWindowsByScope.set(scopeId, stillOpen);
      } else {
        this.openWindowsByScope.delete(scopeId);
        this.slammedScopes.delete(scopeId);
        this.cancelByScope.delete(scopeId);
        this.armedNextRunScopes.delete(scopeId);
      }
    }
  }

  /**
   * A count-down of `seconds`, scaled as an unscoped sleep is, that its caller ends with `cancel` (its ticker listener
   * goes with it, and `done` never resolves): a bound raced against other work, gone once that work is done. No scope:
   * no slam finishes it.
   */
  public countdown(seconds: number): TimerSleep {
    return this.timer.sleep(Math.max(0, this.scaleForScope(seconds)));
  }

  public sleep(seconds: number, scopeId?: ScopeId): Promise<void> {
    if (scopeId && this.slammedScopes.has(scopeId)) return Promise.resolve();

    const scaled = this.scaleForScope(seconds, scopeId);
    const timer = this.timer.sleep(Math.max(0, scaled));
    if (!scopeId) return timer.done;

    return new Promise<void>((resolve) => {
      let done = false;
      const cancel = () => {
        if (done) return;
        done = true;
        // The count stops with the wait. Until 2026-09-26 a slammed sleep kept ticking on the ticker
        // to its original end (review finding 62).
        timer.cancel();
        resolve();
      };
      this.registerCancelable(scopeId, cancel);
      void timer.done.then(() => {
        if (done) return;
        done = true;
        this.unregisterCancelable(scopeId, cancel);
        resolve();
      });
    });
  }

  /**
   * Drives t01 from 0→1 over `seconds` (scaled), calling onUpdate every frame.
   * Multiple concurrent calls in the same scope are independent.
   */
  public tweenProgress(
    seconds: number,
    onUpdate: (t01: number) => void,
    scopeId?: ScopeId,
    options?: TweenProgressOptions,
  ): Promise<void> {
    const easing = resolveEasing(options?.easing, "quadOut");
    const finishOnCancel = options?.finishOnCancel ?? true;

    if (scopeId && this.slammedScopes.has(scopeId)) {
      if (finishOnCancel) safeCall(() => onUpdate(1));
      return Promise.resolve();
    }

    const scaled = this.scaleForScope(seconds, scopeId);
    if (scaled <= 0) {
      safeCall(() => onUpdate(1));
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      let done = false;
      const finish = (finalT?: number) => {
        if (done) return;
        done = true;
        tw.stop();
        if (scopeId) this.unregisterCancelable(scopeId, cancel);
        if (typeof finalT === "number") safeCall(() => onUpdate(finalT));
        resolve();
      };
      const cancel = (): void => (finishOnCancel ? finish(1) : finish());

      const tw = this.startTween(Math.max(0.001, scaled), easing, (t) => safeCall(() => onUpdate(t)), () => finish());

      if (scopeId) {
        this.registerCancelable(scopeId, cancel);
      }
    });
  }

  /**
   * Continuous per-frame tick for INDEFINITE motion (e.g. the reel scroll).
   * `onTick(elapsedSeconds)` receives LOGICAL seconds from 0, clock-scaled like
   * every runner duration. Runs until `isDone()` is true; resolves WITHOUT a
   * final snap. Honors the scope's slam.
   */
  public tickInScope(scopeId: ScopeId, onTick: (elapsedSeconds: number) => void, isDone: () => boolean): Promise<void> {
    if (scopeId && this.slammedScopes.has(scopeId)) return Promise.resolve();

    return new Promise<void>((resolve) => {
      let done = false;
      let logical = 0;
      // Logical seconds advance faster than engine seconds under turbo: a
      // duration D scales to D/s, so logical time runs at s× engine time.
      const rate = this.getTimeScale(scopeId);

      const unsubscribe = this.ticker.add((delta) => {
        if (done) return;
        logical += delta * rate;
        safeCall(() => onTick(logical));
        if (isDone()) finish();
      });

      const finish = () => {
        if (done) return;
        done = true;
        unsubscribe();
        if (scopeId) this.unregisterCancelable(scopeId, finish);
        resolve();
      };

      if (scopeId) this.registerCancelable(scopeId, finish);
    });
  }

  /**
   * Runner-owned position and/or scale tween with channel isolation: starting a
   * position tween on a target stops ONLY the runner's previous position tween
   * on that target, never its scale tween nor anything foreign.
   */
  public tweenTo(target: TweenTarget, seconds: number, props: TweenProps, scopeId?: ScopeId, options?: TweenOptions): Promise<void> {
    const easing = resolveEasing(options?.easing, "quadOut");

    if (scopeId && this.slammedScopes.has(scopeId)) {
      return this.finishFast(target, props, scopeId, easing);
    }

    const scaled = this.scaleForScope(seconds, scopeId);

    return new Promise<void>((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        if (scopeId) this.unregisterCancelable(scopeId, cancel);
        resolve();
      };
      const cancel = (): void => {
        this.stopIfOwned(target, props, tw);
        void this.finishFast(target, props, undefined, easing).then(settle);
      };

      const tw = this.startPropsTween(target, Math.max(0, scaled), props, easing, settle);

      if (scopeId) {
        this.registerCancelable(scopeId, cancel);
      }
    });
  }

  /**
   * Slam this scope: latch it as slammed for the current run window and cancel
   * every registered task (they fast-finish). Returns false when nothing was running.
   */
  public slamFinishScope(scopeId: ScopeId): boolean {
    this.ensureScope(scopeId);

    const set = this.cancelByScope.get(scopeId);
    if (!set || set.size === 0) return false;

    this.slammedScopes.add(scopeId);
    this.log?.(`[PresentationTaskRunner] slamFinishScope scope=${scopeId} cancel=${set.size} finish=${this.slamFinishSeconds}s`);

    for (const cancel of Array.from(set.values())) {
      safeCall(cancel);
    }
    set.clear();
    return true;
  }

  public armSlamNextRun(scopeId: ScopeId): void {
    this.armedNextRunScopes.add(scopeId);
  }

  public disarmSlamNextRun(scopeId: ScopeId): void {
    this.armedNextRunScopes.delete(scopeId);
  }

  /** Fully resets a scope's slam state, cancelables, next-run arm and fixed scale. */
  public clearScope(scopeId: ScopeId): void {
    this.slammedScopes.delete(scopeId);
    this.cancelByScope.delete(scopeId);
    this.armedNextRunScopes.delete(scopeId);
    this.scaleByScope.delete(scopeId);
  }

  /**
   * Registers a FIXED duration scale for `scopeId` that overrides the global
   * speed-mode clock (scale 1 = speed-invariant), for its durations and its
   * `getTimeScale(scopeId)`. Still pause/step/slam aware.
   */
  public setScopeScale(scopeId: ScopeId, scale: number): void {
    this.scaleByScope.set(scopeId, scale);
  }

  public clearScopeScale(scopeId: ScopeId): void {
    this.scaleByScope.delete(scopeId);
  }

  /**
   * Engine seconds so far — the presentation's "now", which pauses and frame-steps with every
   * timeline (the ticker's running total). For code that compares moments; never the wall clock.
   */
  public get engineSeconds(): number {
    return this.ticker.elapsedSeconds;
  }

  /**
   * The rate `scopeId` plays at, in logical seconds per engine second (1 = normal, >1 = turbo): the inverse of its
   * duration scaling, so its `setScopeScale` scale where one is set, else the clock's. A Spine clip set at this rate
   * ends when a `sleep` of its length in the same scope does (review 2026-09-27 #58).
   */
  public getTimeScale(scopeId?: ScopeId): number {
    const scaledOne = this.scaleForScope(1, scopeId);
    return scaledOne > 0 ? 1 / scaledOne : 1;
  }

  /** Registered cancelables per scope (non-empty scopes only) — the debug hook's leak report. */
  public getActiveScopes(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [scope, set] of this.cancelByScope) if (set.size > 0) out[scope] = set.size;
    return out;
  }

  /** Number of registered cancelables for a scope — a leak check for tests and the debug hook. */
  public getActiveTaskCount(scopeId?: ScopeId): number {
    if (scopeId !== undefined) return this.cancelByScope.get(scopeId)?.size ?? 0;
    let total = 0;
    for (const set of this.cancelByScope.values()) total += set.size;
    return total;
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private finishFast(target: TweenTarget, props: TweenProps, scopeId: ScopeId | undefined, easing: EasingFunction): Promise<void> {
    if (!props.position && !props.scale) return Promise.resolve();
    const d = Math.max(0.001, this.slamFinishSeconds);

    return new Promise<void>((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        if (scopeId) this.unregisterCancelable(scopeId, cancel);
        resolve();
      };
      const cancel = (): void => {
        this.stopIfOwned(target, props, tw);
        if (props.position) target.position.set(props.position.x, props.position.y);
        if (props.scale) target.scale.set(props.scale.x, props.scale.y);
        settle();
      };

      const tw = this.startPropsTween(target, d, props, easing, settle);

      if (scopeId) {
        this.registerCancelable(scopeId, cancel);
      }
    });
  }

  /** Starts a position/scale tween on `target`, claiming the touched channels. */
  private startPropsTween(target: TweenTarget, seconds: number, props: TweenProps, easing: EasingFunction, onComplete: () => void): RunningTween {
    if (props.position) this.stopRunnerPositionTween(target);
    if (props.scale) this.stopRunnerScaleTween(target);

    const fromPos = { x: target.position.x, y: target.position.y };
    const fromScale = { x: target.scale.x, y: target.scale.y };
    const toPos = props.position;
    const toScale = props.scale;

    const apply = (t: number): void => {
      if (toPos) target.position.set(lerp(fromPos.x, toPos.x, t), lerp(fromPos.y, toPos.y, t));
      if (toScale) target.scale.set(lerp(fromScale.x, toScale.x, t), lerp(fromScale.y, toScale.y, t));
    };

    const tw: RunningTween = this.startTween(seconds, easing, apply, () => {
      this.releaseIfOwned(target, props, tw);
      onComplete();
    });

    if (toPos) this.activePositionTweenByTarget.set(target, tw);
    if (toScale) this.activeScaleTweenByTarget.set(target, tw);
    return tw;
  }

  private stopIfOwned(target: TweenTarget, props: TweenProps, tw: RunningTween): void {
    if (props.position && this.activePositionTweenByTarget.get(target) === tw) {
      tw.stop();
      this.activePositionTweenByTarget.delete(target);
    }
    if (props.scale && this.activeScaleTweenByTarget.get(target) === tw) {
      tw.stop();
      this.activeScaleTweenByTarget.delete(target);
    }
  }

  private releaseIfOwned(target: TweenTarget, props: TweenProps, tw: RunningTween): void {
    if (props.position && this.activePositionTweenByTarget.get(target) === tw) this.activePositionTweenByTarget.delete(target);
    if (props.scale && this.activeScaleTweenByTarget.get(target) === tw) this.activeScaleTweenByTarget.delete(target);
  }

  private stopRunnerPositionTween(target: TweenTarget): void {
    const existing = this.activePositionTweenByTarget.get(target);
    if (!existing) return;
    existing.stop();
    this.activePositionTweenByTarget.delete(target);
  }

  private stopRunnerScaleTween(target: TweenTarget): void {
    const existing = this.activeScaleTweenByTarget.get(target);
    if (!existing) return;
    existing.stop();
    this.activeScaleTweenByTarget.delete(target);
  }

  /**
   * The in-house tween: eased 0→1 over `seconds` of ENGINE time on the ticker.
   * `seconds <= 0` completes on the next frame with t=1. Completion always
   * delivers exactly t=1 once.
   */
  private startTween(seconds: number, easing: EasingFunction, onUpdate: (t: number) => void, onComplete: () => void): RunningTween {
    let elapsed = 0;
    let stopped = false;
    const unsubscribe = this.ticker.add((delta) => {
      if (stopped) return;
      elapsed += delta;
      const k = seconds > 0 ? Math.min(1, elapsed / seconds) : 1;
      if (k >= 1) {
        stopped = true;
        unsubscribe();
        onUpdate(1);
        onComplete();
        return;
      }
      onUpdate(easing(k));
    });
    return {
      stop(): void {
        if (stopped) return;
        stopped = true;
        unsubscribe();
      },
    };
  }

  private ensureScope(scopeId: ScopeId): void {
    if (!this.cancelByScope.has(scopeId)) this.cancelByScope.set(scopeId, new Set());
  }

  /** Duration scaling: the scope's fixed scale when set, else the global clock. */
  private scaleForScope(seconds: number, scopeId?: ScopeId): number {
    if (scopeId !== undefined) {
      const scale = this.scaleByScope.get(scopeId);
      if (scale !== undefined) return scale > 0 ? seconds / scale : seconds;
    }
    return this.clock.scaleDurationSeconds(seconds);
  }

  private registerCancelable(scopeId: ScopeId, cancel: Cancelable): void {
    this.ensureScope(scopeId);
    this.cancelByScope.get(scopeId)!.add(cancel);
  }

  /** A task that completed NATURALLY drops out of its scope, so a long-lived scope doesn't accumulate
   *  finished entries (and `getActiveTaskCount` means "still running"). A cancelled task is removed by
   *  `slamFinishScope`/`clearScope` themselves. */
  private unregisterCancelable(scopeId: ScopeId, cancel: Cancelable): void {
    this.cancelByScope.get(scopeId)?.delete(cancel);
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function safeCall(fn: () => void): void {
  try {
    fn();
  } catch {
    // Presentation callbacks must never break the timeline.
  }
}
